import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, CalendarDays, Clock, Plus, X, MapPin, Users,
} from 'lucide-react';
import { T, ROLES, springs } from '../lib/tokens';
import { API_URL, apiFetch } from '../lib/api';
import { supabase } from '../lib/supabase';
import { Glass } from '../components/glass/Glass';
import { RoleBadge } from '../components/primitives/Badge';
import { TaskPopover } from '../components/cards/TaskPopover';
import { ViewHero } from '../components/cards/ViewHero';
import { fmtMinutes, timeFromMin } from '../lib/helpers';

const HOUR_HEIGHT = 60; // px per hour
const START_HOUR = 7;
const END_HOUR = 22;

function getWeekDates(offset = 0) {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) + offset * 7);

  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(d);
  }
  return dates;
}

function dateKey(d) {
  return d.toISOString().split('T')[0];
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ─── Calendar Day Column ────────────────────────────────────────────────────

const DayColumn = ({ date, blocks, calEvents, isToday, onSelectTask, onClickSlot }) => {
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();

  const handleClick = (e) => {
    // Calculate which time slot was clicked
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const clickedMin = START_HOUR * 60 + Math.round(y / (HOUR_HEIGHT / 60));
    // Snap to 15-minute intervals
    const snapped = Math.round(clickedMin / 15) * 15;
    if (onClickSlot) onClickSlot(date, snapped);
  };

  return (
    <div style={{ position: 'relative', height: (END_HOUR - START_HOUR) * HOUR_HEIGHT, flex: 1, minWidth: 0, cursor: 'crosshair' }} onClick={handleClick}>
      {/* Calendar events */}
      {calEvents.map((ev, i) => {
        const top = Math.max(0, (ev.start_min - START_HOUR * 60) * (HOUR_HEIGHT / 60));
        const height = Math.max(20, (ev.end_min - ev.start_min) * (HOUR_HEIGHT / 60));
        const r = ev.role_slug ? ROLES[ev.role_slug] : null;
        const tooltip = [
          ev.title,
          `${timeFromMin(ev.start_min)} - ${timeFromMin(ev.end_min)}`,
          ev.location ? `📍 ${ev.location}` : null,
          ev.attendee_count ? `👥 ${ev.attendee_count} attendees` : null,
        ].filter(Boolean).join('\n');

        return (
          <div key={`ev-${i}`} title={tooltip} style={{
            position: 'absolute', top, left: 2, right: 2, height,
            background: r ? r.light : 'rgba(74,158,204,0.08)',
            border: `1px solid ${r ? r.border : 'rgba(74,158,204,0.20)'}`,
            borderLeft: `3px solid ${r ? r.color : T.blue}`,
            borderRadius: 6, padding: '3px 6px', overflow: 'hidden',
            zIndex: 1,
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.text, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ev.title}
            </div>
            {height > 28 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: T.textMuted, marginTop: 1, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                <span>{timeFromMin(ev.start_min)}</span>
                {ev.attendee_count > 1 && height > 40 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <Users size={8} /> {ev.attendee_count}
                  </span>
                )}
              </div>
            )}
            {ev.location && height > 52 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: T.textMuted, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <MapPin size={8} style={{ flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.location}</span>
              </div>
            )}
          </div>
        );
      })}

      {/* Scheduled task blocks */}
      {blocks.map((block, i) => {
        const top = Math.max(0, (block.start_min - START_HOUR * 60) * (HOUR_HEIGHT / 60));
        const height = Math.max(20, (block.end_min - block.start_min) * (HOUR_HEIGHT / 60));
        const task = block.tasks;
        const roleSlug = task?.roles?.slug;
        const r = roleSlug ? ROLES[roleSlug] : null;
        const isLocked = block.is_locked;
        const isOoh = block.out_of_hours;

        return (
          <motion.div
            key={`tb-${i}`}
            whileHover={{ scale: 1.02, zIndex: 10 }}
            onClick={() => task && onSelectTask(task, block)}
            title={isOoh ? 'Scheduled outside working hours (hard deadline)' : undefined}
            style={{
              position: 'absolute', top, left: 2, right: 2, height,
              background: isOoh
                ? 'repeating-linear-gradient(135deg, rgba(255,164,86,0.18) 0 6px, rgba(255,164,86,0.06) 6px 12px)'
                : isLocked ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.80)',
              border: `1px solid ${isOoh ? T.amberBorder : isLocked ? T.amberBorder : 'rgba(0,0,0,0.06)'}`,
              borderLeft: `3px solid ${task?.priority === 1 ? T.red : (r?.color || T.textLight)}`,
              borderRadius: 6, padding: '3px 6px', overflow: 'hidden',
              cursor: 'pointer', zIndex: 2,
              boxShadow: T.shadowSm,
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 600, color: T.text, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {task?.title || 'Task'}
            </div>
            {height > 28 && (
              <div style={{ display: 'flex', gap: 4, marginTop: 2, alignItems: 'center' }}>
                {r && <span style={{ fontSize: 8, fontWeight: 700, padding: '0 4px', borderRadius: 3, background: r.light, color: r.color }}>{r.label}</span>}
                <span style={{ fontSize: 9, color: T.textMuted }}>
                  {timeFromMin(block.start_min)}
                </span>
                {isLocked && <span style={{ fontSize: 8, color: T.amber }}>locked</span>}
                {isOoh && <span style={{ fontSize: 8, fontWeight: 700, color: T.amber, background: T.amberLight, padding: '0 3px', borderRadius: 3 }}>OOH</span>}
              </div>
            )}
          </motion.div>
        );
      })}

      {/* Current time line */}
      {isToday && nowMin >= START_HOUR * 60 && nowMin <= END_HOUR * 60 && (
        <div style={{
          position: 'absolute',
          top: (nowMin - START_HOUR * 60) * (HOUR_HEIGHT / 60),
          left: -4, right: 0, zIndex: 20,
          display: 'flex', alignItems: 'center',
        }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.red, flexShrink: 0 }} />
          <div style={{ flex: 1, height: 2, background: T.red }} />
        </div>
      )}
    </div>
  );
};

// ─── Main CalendarView ──────────────────────────────────────────────────────

export const CalendarView = ({ scheduledBlocks, liveCalendar, onUpdate, triggerScheduler }) => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [calData, setCalData] = useState({ blocks: [], calendarEvents: [] });
  const [selectedTask, setSelectedTask] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [quickCreate, setQuickCreate] = useState(null); // { date, startMin }
  const [quickTitle, setQuickTitle] = useState('');
  const [quickDuration, setQuickDuration] = useState(30);
  const [quickCreating, setQuickCreating] = useState(false);

  const handleSlotClick = (date, startMin) => {
    setQuickCreate({ date: dateKey(date), startMin });
    setQuickTitle('');
    setQuickDuration(30);
  };

  const createQuickTask = async () => {
    if (!quickTitle.trim() || !quickCreate) return;
    setQuickCreating(true);
    // Insert task
    const { data: newTask } = await supabase.from('tasks').insert({
      title: quickTitle.trim(),
      priority: 2,
      duration_minutes: quickDuration,
      status: 'todo',
      done: false,
      auto_schedule: false, // Manually placed
      is_locked: true,
      locked_at: new Date().toISOString(),
      sort_order: 0,
    }).select().single();

    if (newTask) {
      // Insert locked scheduled block
      await supabase.from('scheduled_blocks').insert({
        task_id: newTask.id,
        date: quickCreate.date,
        start_min: quickCreate.startMin,
        end_min: quickCreate.startMin + quickDuration,
        is_locked: true,
        schedule_version: Date.now(),
      });
    }
    setQuickCreate(null);
    setQuickCreating(false);
    onUpdate();
    if (triggerScheduler) triggerScheduler();
  };

  const syncToGoogleCalendar = async () => {
    if (!API_URL || syncing) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const todayStr = new Date().toISOString().split('T')[0];
      const res = await apiFetch('/calendar/sync-tasks', {
        method: 'POST',
        body: JSON.stringify({ date: todayStr, account: 'michaelpryke' }),
      });
      const data = await res.json();
      setSyncResult(data.ok ? `Synced ${data.synced} tasks` : (data.error || 'Sync failed'));
      setTimeout(() => setSyncResult(null), 3000);
    } catch (err) {
      setSyncResult('Sync failed');
      setTimeout(() => setSyncResult(null), 3000);
    }
    setSyncing(false);
  };
  const [now, setNow] = useState(new Date());

  const weekDates = getWeekDates(weekOffset);
  const todayStr = dateKey(new Date());

  // Tick every minute for the now line
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch blocks AND live calendar events for the week (in parallel)
  useEffect(() => {
    if (!API_URL) {
      // Fallback: use data from useSupabaseData
      setCalData({ blocks: scheduledBlocks || [], calendarEvents: [] });
      return;
    }
    const startDate = dateKey(weekDates[0]);

    Promise.all([
      apiFetch(`/scheduler/blocks?start=${startDate}&days=7`)
        .then(r => r.ok ? r.json() : { blocks: [], calendarEvents: [] })
        .catch(() => ({ blocks: [], calendarEvents: [] })),
      // Live Google Calendar events across all 3 accounts. This is what makes
      // the week grid show real events (not just scheduled task blocks).
      apiFetch(`/calendar/range?start=${startDate}&days=7`)
        .then(r => r.ok ? r.json() : { events: [] })
        .catch(() => ({ events: [] })),
    ]).then(([blocksData, rangeData]) => {
      // Prefer live events; fall back to whatever the scheduler cached
      const liveEvents = rangeData.events || [];
      const cachedEvents = blocksData.calendarEvents || [];
      const calendarEvents = liveEvents.length > 0 ? liveEvents : cachedEvents;
      setCalData({
        blocks: blocksData.blocks || [],
        calendarEvents,
      });
    });
  }, [weekOffset, scheduledBlocks]);

  const hours = [];
  for (let h = START_HOUR; h <= END_HOUR; h++) hours.push(h);

  const weekLabel = `${weekDates[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${weekDates[6].toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      {/* Hero */}
      {(() => {
        const todayBlocks = (calData.blocks || []).filter(b => b.date === todayStr);
        const todayEvents = (calData.calendarEvents || []).filter(e => e.date === todayStr);
        const weekEvents = (calData.calendarEvents || []).length;
        const weekBlocks = (calData.blocks || []).length;
        return (
          <ViewHero
            icon={CalendarDays}
            gradient="linear-gradient(135deg, #B89BD8 0%, #8B72C8 100%)"
            title="Calendar"
            subtitle={weekLabel}
            stats={[
              { label: `${todayBlocks.length + todayEvents.length} today`, color: T.violet, bg: 'rgba(139,114,200,0.10)', border: 'rgba(139,114,200,0.20)' },
              { label: `${weekEvents} events`, color: T.blue, bg: 'rgba(74,158,204,0.10)', border: 'rgba(74,158,204,0.18)' },
              { label: `${weekBlocks} task blocks`, color: T.amber, bg: 'rgba(212,146,30,0.10)', border: 'rgba(212,146,30,0.18)' },
            ]}
            right={(
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => setWeekOffset(0)} style={{
                  padding: '6px 14px', borderRadius: 999, border: '1px solid rgba(0,0,0,0.08)',
                  background: weekOffset === 0 ? 'rgba(255,255,255,0.7)' : 'transparent',
                  cursor: 'pointer', fontSize: 12, fontWeight: 500, color: T.textMid, fontFamily: 'inherit',
                }}>Today</button>
                <button onClick={() => setWeekOffset(w => w - 1)} style={{
                  padding: '6px 8px', borderRadius: 999, border: '1px solid rgba(0,0,0,0.08)',
                  background: 'transparent', cursor: 'pointer', color: T.textMid,
                }}><ChevronLeft size={16} /></button>
                <button onClick={() => setWeekOffset(w => w + 1)} style={{
                  padding: '6px 8px', borderRadius: 999, border: '1px solid rgba(0,0,0,0.08)',
                  background: 'transparent', cursor: 'pointer', color: T.textMid,
                }}><ChevronRight size={16} /></button>
                <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.08)', margin: '0 4px' }} />
                <button onClick={syncToGoogleCalendar} disabled={syncing} style={{
                  padding: '6px 14px', borderRadius: 999, border: '1px solid rgba(0,0,0,0.08)',
                  background: syncing ? 'rgba(255,255,255,0.7)' : 'transparent',
                  cursor: 'pointer', fontSize: 12, fontWeight: 500, color: T.textMid, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <CalendarDays size={12} />
                  {syncing ? 'Syncing...' : 'Sync to Google'}
                </button>
                {syncResult && (
                  <span style={{ fontSize: 11, color: syncResult.includes('Synced') ? T.green : T.red, fontWeight: 500 }}>
                    {syncResult}
                  </span>
                )}
              </div>
            )}
          />
        );
      })()}

      {/* Week Grid */}
      <Glass style={{ padding: 0, overflow: 'hidden' }} hover={false}>
        {/* Day headers */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <div style={{ width: 52, flexShrink: 0 }} />
          {weekDates.map((d, i) => {
            const isToday = dateKey(d) === todayStr;
            return (
              <div key={i} style={{
                flex: 1, textAlign: 'center', padding: '10px 0',
                borderLeft: i > 0 ? '1px solid rgba(0,0,0,0.04)' : 'none',
              }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {DAY_NAMES[i]}
                </div>
                <div style={{
                  fontSize: 18, fontWeight: 700,
                  color: isToday ? T.amber : T.text,
                  marginTop: 2,
                }}>
                  {d.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Time grid */}
        <div style={{ display: 'flex', position: 'relative', overflowY: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
          {/* Hour labels */}
          <div style={{ width: 52, flexShrink: 0, position: 'relative', height: (END_HOUR - START_HOUR) * HOUR_HEIGHT }}>
            {hours.map(h => (
              <div key={h} style={{
                position: 'absolute', top: (h - START_HOUR) * HOUR_HEIGHT,
                width: '100%', textAlign: 'right', paddingRight: 8,
              }}>
                <span style={{
                  fontSize: 10, color: T.textLight, fontWeight: 500,
                  fontFamily: "'SF Mono', Menlo, Monaco, monospace",
                  fontFeatureSettings: '"tnum"',
                }}>
                  {`${String(h).padStart(2, '0')}:00`}
                </span>
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDates.map((d, i) => {
            const dk = dateKey(d);
            const dayBlocks = (calData.blocks || []).filter(b => b.date === dk);
            const dayEvents = (calData.calendarEvents || []).filter(e => e.date === dk);

            return (
              <div key={i} style={{
                flex: 1, position: 'relative',
                borderLeft: '1px solid rgba(0,0,0,0.04)',
                height: (END_HOUR - START_HOUR) * HOUR_HEIGHT,
              }}>
                {/* Hour grid lines */}
                {hours.map(h => (
                  <div key={h} style={{
                    position: 'absolute', top: (h - START_HOUR) * HOUR_HEIGHT,
                    left: 0, right: 0, borderTop: '1px solid rgba(0,0,0,0.03)',
                  }} />
                ))}

                <DayColumn
                  date={d}
                  blocks={dayBlocks}
                  calEvents={dayEvents}
                  isToday={dk === todayStr}
                  onSelectTask={(task, block) => setSelectedTask({ task, block })}
                  onClickSlot={handleSlotClick}
                />
              </div>
            );
          })}
        </div>
      </Glass>

      {/* Task popover */}
      {selectedTask && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setSelectedTask(null)}>
          <div onClick={e => e.stopPropagation()}>
            <TaskPopover
              task={selectedTask.task}
              block={selectedTask.block}
              anchorRect={{ top: 200, left: 400 }}
              onClose={() => setSelectedTask(null)}
              onUpdate={() => { setSelectedTask(null); onUpdate(); if (triggerScheduler) triggerScheduler(); }}
            />
          </div>
        </div>
      )}

      {/* Quick create modal */}
      <AnimatePresence>
        {quickCreate && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setQuickCreate(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <motion.div
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              style={{ width: 380, background: '#fff', borderRadius: 14, padding: 22, boxShadow: T.shadowLg }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
                  Quick Add — {new Date(quickCreate.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} at {timeFromMin(quickCreate.startMin)}
                </h3>
                <button onClick={() => setQuickCreate(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textLight }}><X size={16} /></button>
              </div>
              <input
                value={quickTitle} onChange={e => setQuickTitle(e.target.value)}
                placeholder="Task title..."
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') createQuickTask(); }}
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.10)',
                  fontSize: 13, fontFamily: 'inherit', outline: 'none', marginBottom: 10,
                }}
              />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
                <Clock size={12} color={T.textMuted} />
                <select value={quickDuration} onChange={e => setQuickDuration(Number(e.target.value))} style={{
                  border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, padding: '4px 8px',
                  fontSize: 12, color: T.textMid, fontFamily: 'inherit', background: '#fff',
                }}>
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                  <option value={240}>4 hours</option>
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setQuickCreate(null)} style={{
                  padding: '7px 16px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
                  background: 'transparent', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', color: T.textMid,
                }}>Cancel</button>
                <button onClick={createQuickTask} disabled={!quickTitle.trim() || quickCreating} style={{
                  padding: '7px 16px', borderRadius: 8, border: 'none',
                  background: T.amber, color: '#fff', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                  opacity: quickTitle.trim() && !quickCreating ? 1 : 0.5,
                }}>
                  <Plus size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  {quickCreating ? 'Creating...' : 'Add Task'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
